// src/modules/documents/layout/groupPanelsByContent.js
//
// Multi-meter Tek Hat Şeması için panel gruplama:
// Aynı branch circuit imzasına sahip paneller tek bir belgede gösterilir.
// Örnek: NKT1, NKT3, NKT5 aynı içeriğe sahipse → tek "NKT1-NKT3-NKT5" belgesi.

import { COMPONENT_KINDS } from '../../../types/SimpleSupplyPoint';
import { expandSimpleSupplyForFloorCopies } from '../../../utils/simpleTransformFunctions';

/**
 * Bir panelin branch circuit imzasını hesaplar.
 * Yüklerin sıralaması önemli değil — önce sort edilir.
 */
function computePanelSignature(panelId, components, feeders) {
  const loads = components.filter(
    c => c.kind === COMPONENT_KINDS.LOAD && c.parentComponentId === panelId
  );
  const tokens = loads.map(load => {
    const feeder = feeders.find(f => f.to === load.id || f.targetId === load.id);
    const type   = load.metadata?.branchType || load.metadata?.loadType || 'unknown';
    const current = feeder?.protection?.mcb?.current ?? 0;
    const section = feeder?.cable?.section ?? feeder?.cable?.indexNo ?? 0;
    const phase   = feeder?.protection?.mcb?.phaseCount ?? 1;
    return `${type}:${current}:${section}:${phase}`;
  });
  tokens.sort();
  return tokens.join('|') || '__empty__';
}

/**
 * simpleSupply içindeki non-main panelleri branch circuit imzasına göre gruplar.
 * Kat kopyaları (expandSimpleSupplyForFloorCopies) da dahil edilir.
 *
 * @param {object} simpleSupply  - Redux simpleSupply state
 * @param {array}  floorPlans   - Kat planı listesi (sanal kopyalar için gerekli)
 * @returns {{ panelNames: string[], representativePanelId: string }[]}
 */
export function groupPanelsByContent(simpleSupply, floorPlans) {
  if (!simpleSupply?.components?.length) return [];

  // Kat kopyalarını genişlet
  const expanded = expandSimpleSupplyForFloorCopies(simpleSupply, floorPlans || []);
  const { components, feeders } = expanded;

  const panels = components.filter(
    c => c.kind === COMPONENT_KINDS.DIST_PANEL || c.kind === COMPONENT_KINDS.PANEL
  );
  if (panels.length === 0) return [];

  // İmzaları önceden hesapla
  const panelsWithSig = panels.map(p => ({
    id:            p.id,
    name:          p.name || p.id,
    isVirtualCopy: !!p.metadata?._virtualCopy,
    sig:           computePanelSignature(p.id, components, feeders),
  }));

  const groups = [];
  const usedIds = new Set();

  for (const p of panelsWithSig) {
    if (usedIds.has(p.id)) continue;

    const same = panelsWithSig.filter(q => !usedIds.has(q.id) && q.sig === p.sig);
    // Orijinaller önce → representative olarak kullanılır (ID orijinal supply'da mevcut)
    same.sort((a, b) => (a.isVirtualCopy ? 1 : 0) - (b.isVirtualCopy ? 1 : 0));
    same.forEach(q => usedIds.add(q.id));

    groups.push({
      panelNames:             same.map(q => q.name),
      representativePanelId:  same[0].id,
    });
  }

  return groups;
}
